import { createRootRoute, Outlet, Link } from '@tanstack/react-router';
import { ShoppingCart, User, LogOut, Settings, Store, Moon, Sun } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuthStore, useCartStore, useThemeStore } from '../store';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { Toaster } from 'sonner';
import { useEffect } from 'react';
import { PickupNotifier } from '../components/PickupNotifier';

export const Route = createRootRoute({
  component: RootComponent,
});

function ThemeToggle() {
  const { theme, setTheme } = useThemeStore();
  
  useEffect(() => {
    const isDark = 
      theme === 'dark' || 
      (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return (
    <button
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      className="p-2 rounded-full text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

function RootComponent() {
  const { user, profile } = useAuthStore();
  const cartItems = useCartStore((state) => state.items);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex flex-col selection:bg-neutral-900 selection:text-white dark:selection:bg-white dark:selection:text-neutral-900">
      <Helmet>
        <title>Cardston Market</title>
        <html lang="en" />
        <meta name="description" content="Shop handmade goods from local Alberta artisans on Cardston Market. Discover unique crafts, artisan foods & local products from Cardston, AB. Support local." />
        <meta name="keywords" content="local market, handcrafted goods, artisan products, local vendors, buy local, Cardston, community marketplace, handemade, pickup orders" />
        <link rel="canonical" href="https://www.cardstonmarket.com/" />
        <link rel="alternate" hrefLang="en-CA" href="https://www.cardstonmarket.com/" />
        <link rel="alternate" hrefLang="x-default" href="https://www.cardstonmarket.com/" />
        <meta property="og:title" content="Cardston Market — Shop Local Alberta Artisans &amp; Handmade Goods Online" />
        <meta property="og:description" content="Shop handmade goods from local Alberta artisans on Cardston Market. Discover unique crafts, artisan foods & local products from Cardston, AB." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cardstonmarket.com/" />
        <meta property="og:site_name" content="Cardston Market" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://www.cardstonmarket.com/og-banner.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cardston Market — Shop Local Alberta Artisans &amp; Handmade Goods Online" />
        <meta name="twitter:description" content="Shop handmade goods from local Alberta artisans on Cardston Market. Discover unique crafts, artisan foods & local products from Cardston, AB." />
        <meta name="twitter:site" content="@cardstonmarket" />
        <meta name="twitter:image" content="https://www.cardstonmarket.com/og-banner.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["LocalBusiness", "OnlineStore"],
              "@id": "https://www.cardstonmarket.com/#localbusiness",
              "name": "Cardston Market",
              "description": "Cardston Market is an e-commerce platform for local artisans in Cardston, Alberta, Canada. Shop handmade goods, artisan foods, and unique local products online.",
              "url": "https://www.cardstonmarket.com",
              "logo": "https://www.cardstonmarket.com/dashboard-1.png",
              "image": "https://www.cardstonmarket.com/dashboard-1.png",
              "telephone": "+1-403-555-0199",
              "email": "support@cardstonmarket.com",
              "currenciesAccepted": "CAD",
              "paymentAccepted": "Credit Card, Stripe",
              "priceRange": "$$",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Main Street",
                "addressLocality": "Cardston",
                "addressRegion": "AB",
                "postalCode": "T0L 0K0",
                "addressCountry": "CA"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 49.2003,
                "longitude": -113.3005
              },
              "areaServed": [
                {
                  "@type": "City",
                  "name": "Cardston",
                  "containedInPlace": {
                    "@type": "AdministrativeArea",
                    "name": "Alberta",
                    "containedInPlace": {
                      "@type": "Country",
                      "name": "Canada"
                    }
                  }
                },
                {
                  "@type": "State",
                  "name": "Alberta"
                }
              ],
              "openingHoursSpecification": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                "opens": "09:00",
                "closes": "18:00"
              },
              "sameAs": [],
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@cardstonmarket.com",
                "contactType": "customer service",
                "areaServed": "CA",
                "availableLanguage": ["English", "French"]
              },
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Local Artisan Products",
                "itemListElement": [
                  { "@type": "OfferCatalog", "name": "Handmade Goods" },
                  { "@type": "OfferCatalog", "name": "Artisan Foods" },
                  { "@type": "OfferCatalog", "name": "Home Decor" },
                  { "@type": "OfferCatalog", "name": "Textiles & Clothing" }
                ]
              }
            },
            {
              "@type": "Organization",
              "@id": "https://www.cardstonmarket.com/#organization",
              "name": "Cardston Market",
              "url": "https://www.cardstonmarket.com",
              "logo": "https://www.cardstonmarket.com/dashboard-1.png",
              "sameAs": [],
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@cardstonmarket.com",
                "contactType": "customer service"
              }
            },
            {
              "@type": "WebSite",
              "@id": "https://www.cardstonmarket.com/#website",
              "name": "Cardston Market",
              "url": "https://www.cardstonmarket.com",
              "publisher": { "@id": "https://www.cardstonmarket.com/#organization" },
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://www.cardstonmarket.com/?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          ]
        })}</script>
      </Helmet>
      <header role="banner" className="sticky top-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="text-2xl font-serif font-semibold tracking-tight text-neutral-900 dark:text-white">
            Cardston Market
          </Link>

          <nav aria-label="Main navigation" className="flex items-center gap-6 sm:gap-8">
            <Link to="/sell" className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white transition-colors">
              <span className="text-sm font-medium hidden md:block">Sell</span>
            </Link>
            <ThemeToggle />
            <Link to="/cart" className="relative text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-2">
              <span className="text-sm font-medium hidden sm:block">Cart</span>
              <ShoppingCart className="w-5 h-5 stroke-[1.5]" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="flex items-center gap-4 sm:gap-6">
                <Link to="/profile" className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white flex items-center gap-1.5 text-sm font-medium transition-colors">
                  <User className="w-4 h-4 stroke-[1.5]" />
                  <span className="hidden sm:block">Profile</span>
                </Link>
                {profile?.isAdmin && (
                  <Link to="/admin" className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white flex items-center gap-1.5 text-sm font-medium transition-colors">
                    <Settings className="w-4 h-4 stroke-[1.5]" />
                    <span className="hidden sm:block">Admin</span>
                  </Link>
                )}
                {profile?.role === 'vendor' && (
                  <Link to="/vendor" className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white flex items-center gap-1.5 text-sm font-medium transition-colors">
                    <Store className="w-4 h-4 stroke-[1.5]" />
                    <span className="hidden sm:block">Vendor</span>
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white flex items-center gap-1.5 text-sm font-medium transition-colors"
                >
                  <LogOut className="w-4 h-4 stroke-[1.5]" />
                  <span className="hidden sm:block">Logout</span>
                </button>
              </div>
            ) : (
              <Link to="/login" className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white flex items-center gap-1.5 text-sm font-medium transition-colors">
                <User className="w-4 h-4 stroke-[1.5]" />
                <span className="hidden sm:block">Account</span>
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main role="main" className="flex-1 w-full flex flex-col">
        <Outlet />
      </main>
      <PickupNotifier />
      <Toaster position="bottom-right" theme={useThemeStore.getState().theme === 'dark' ? 'dark' : 'light'} />

      <footer role="contentinfo" className="bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 mt-auto transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-2xl font-serif font-semibold tracking-tight text-neutral-900 dark:text-white">
              Cardston Market
            </div>
            <div className="flex flex-wrap gap-6 sm:gap-8 text-sm text-neutral-500 dark:text-neutral-400">
              <Link to="/" className="hover:text-neutral-900 dark:hover:text-white transition-colors">Shop</Link>
              <Link to="/sell" className="hover:text-neutral-900 dark:hover:text-white transition-colors">Sell</Link>
              <Link to="/about" className="hover:text-neutral-900 dark:hover:text-white transition-colors">About</Link>
              <Link to="/privacy" className="hover:text-neutral-900 dark:hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-neutral-900 dark:hover:text-white transition-colors">Terms</Link>
              <a href="https://mail.google.com/mail/?view=cm&fs=1&to=support@cardstonmarket.com" target="_blank" rel="noopener noreferrer" className="hover:text-neutral-900 dark:hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-neutral-100 dark:border-neutral-800 text-center text-neutral-400 dark:text-neutral-500 text-xs">
            &copy; {new Date().getFullYear()} Cardston Market. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
